import { useQuery } from '@tanstack/react-query';
import { appQueries } from '../../api/app.api';
import { graphQueries } from '../../api/graph.api';

/**
 * Hook to determine if Entra ID links should be shown.
 * Links are hidden when:
 * - The admin has configured hideEntraIdLinksForNonAdmins in settings AND
 * - The current user is not an admin
 *
 * Note: Since the Microsoft Graph API endpoint /beta/admin/entra/uxSetting is not working
 * (returns authentication errors), this feature relies on a manual admin configuration
 * instead of automatically detecting the "Restrict access to Microsoft Entra admin center" setting.
 *
 * @returns true if links should be shown, false if they should be hidden
 */
const useShowEntraIdLinks = (): boolean => {
  const {
    data: appEnabledInfo,
    isLoading: isLoadingAppInfo,
    isError: isErrorAppInfo,
  } = useQuery({
    ...appQueries.isAppEnabled,
    // Cache for 5 minutes since this is a global setting that rarely changes
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: adminInfo,
    isLoading: isLoadingAdminInfo,
    isError: isErrorAdminInfo,
  } = useQuery({
    ...graphQueries.isAppMgmtAdmin,
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
  });

  // Show links if:
  // 1. Data is still loading (default to showing links during load), OR
  // 2. There was an error fetching data (fail-open approach), OR
  // 3. Hiding is not configured, OR
  // 4. User is an admin
  if (isLoadingAppInfo || isLoadingAdminInfo || isErrorAppInfo || isErrorAdminInfo) {
    return true;
  }

  // At this point, both appEnabledInfo and adminInfo should be available
  if (!appEnabledInfo || !adminInfo) {
    // Defensive check - should not happen but default to showing links
    return true;
  }

  // Show links if hiding is not configured or if user is an admin
  return !appEnabledInfo.settings.hideEntraIdLinksForNonAdmins || adminInfo.isAdmin;
};

export default useShowEntraIdLinks;
